import { supabase } from '@/integrations/supabase/client';

/**
 * Client fuer die Edge Function `manage-subscription` (Bandwechsel + Add-ons
 * fuer BESTANDSKUNDEN). Neukunden ohne aktives Abo laufen weiter ueber den
 * Checkout-Weg (`handleUpgrade` in lib/checkout.ts) — die Function antwortet
 * ihnen mit 409 `NO_ACTIVE_SUBSCRIPTION`, was hier als eigener Fehlertyp
 * ankommt, damit die Aufrufer sauber auf den Checkout umleiten koennen.
 *
 * Die uebrigen erwarteten 409er tragen ebenfalls eigene Fehlertypen
 * (SCHEDULE_PENDING, SUBSCRIPTION_NOT_MANAGEABLE) — die Aufrufer zeigen dafuer
 * spezifische Meldungen und melden sie NICHT nach Sentry (erwartete Zustaende,
 * keine Defekte).
 */

export type ManageSubscriptionResult = {
  status: 'updated' | 'scheduled' | 'released';
  /** Nur bei `scheduled`: wann die Aenderung wirkt (Periodenende, ISO-Datum). */
  effective_at?: string;
};

/** 409 NO_ACTIVE_SUBSCRIPTION → der Aufrufer soll den Checkout-Weg anbieten. */
export class NoActiveSubscriptionError extends Error {
  constructor() {
    super('NO_ACTIVE_SUBSCRIPTION');
    this.name = 'NoActiveSubscriptionError';
  }
}

/**
 * 409 SCHEDULE_PENDING → fuer das Abo ist bereits ein Bandwechsel zum
 * Periodenende geplant; Add-on-/Band-Aenderungen sind bis zum Aufheben oder
 * Stichtag blockiert. Der Abbruch laeuft ueber `setBand(aktuelles Band)` —
 * der Server released den pendenden Schedule dann (`{status:'released'}`).
 */
export class SchedulePendingError extends Error {
  constructor() {
    super('SCHEDULE_PENDING');
    this.name = 'SchedulePendingError';
  }
}

/**
 * 409 SUBSCRIPTION_NOT_MANAGEABLE → das Abo EXISTIERT, ist aber in einem
 * Stripe-Status (past_due etc.), in dem wir es nicht umbauen. Die Abhilfe ist
 * die Zahlungsmethode im Kundenportal, nicht der Checkout. `status` traegt den
 * rohen Stripe-Status fuer die Meldung.
 */
export class SubscriptionNotManageableError extends Error {
  readonly status: string;
  constructor(status: string) {
    super('SUBSCRIPTION_NOT_MANAGEABLE');
    this.name = 'SubscriptionNotManageableError';
    this.status = status;
  }
}

/**
 * 409 SPECIAL_PLAN (UX-Audit D7) → Sondertarife wechseln ihr Band nie selbst,
 * `manage-subscription` lehnt `set_band` dafuer ab. Ein Zustand, kein Defekt:
 * der Aufrufer zeigt den Kontakt-Hinweis und meldet NICHT nach Sentry. Heute
 * erreicht die Oberflaeche den Fall nicht (Sondertarife ohne Stripe-Abo sehen
 * keinen Bandwechsel), der bekannte Servercode darf trotzdem nie als Fehler
 * landen.
 */
export class SpecialPlanConflictError extends Error {
  constructor() {
    super('SPECIAL_PLAN');
    this.name = 'SpecialPlanConflictError';
  }
}

/**
 * Erkennt die 409-Faelle am eigenen Fehlertyp UND am Namen — `instanceof`
 * bricht, sobald das Modul in zwei Bundle-Kopien landet (Muster
 * `_shared/experiment-control-guard.ts`, dort dokumentiert).
 */
export function isNoActiveSubscription(err: unknown): boolean {
  return err instanceof NoActiveSubscriptionError
    || (err instanceof Error && err.name === 'NoActiveSubscriptionError');
}

export function isSchedulePending(err: unknown): boolean {
  return err instanceof SchedulePendingError
    || (err instanceof Error && err.name === 'SchedulePendingError');
}

export function isSubscriptionNotManageable(err: unknown): boolean {
  return err instanceof SubscriptionNotManageableError
    || (err instanceof Error && err.name === 'SubscriptionNotManageableError');
}

export function isSpecialPlanConflict(err: unknown): boolean {
  return err instanceof SpecialPlanConflictError
    || (err instanceof Error && err.name === 'SpecialPlanConflictError');
}

/**
 * Stripe-Status aus einem SubscriptionNotManageableError — name-basiert
 * gelesen (nicht per instanceof, siehe oben), mit `unknown` als Fallback,
 * damit die i18n-Interpolation nie `undefined` druckt.
 */
export function notManageableStatus(err: unknown): string {
  if (!isSubscriptionNotManageable(err)) return 'unknown';
  const status = (err as { status?: unknown }).status;
  return typeof status === 'string' && status.length > 0 ? status : 'unknown';
}

type ManageErrorBody = {
  code?: string;
  error?: unknown;
  status?: unknown;
};

/**
 * Wirft den passenden Fehlertyp fuer einen Server-Body — geteilt vom non-2xx-
 * Pfad (classifyInvokeError) und dem 2xx-Pfad (Body mit error-Feld). Kein
 * Throw, wenn der Body keinen Fehler traegt.
 */
function throwClassifiedBody(body: ManageErrorBody | null | undefined): void {
  if (!body) return;
  if (body.code === 'NO_ACTIVE_SUBSCRIPTION') throw new NoActiveSubscriptionError();
  if (body.code === 'SPECIAL_PLAN' || body.error === 'SPECIAL_PLAN') throw new SpecialPlanConflictError();
  if (body.error === 'SCHEDULE_PENDING') throw new SchedulePendingError();
  if (body.error === 'SUBSCRIPTION_NOT_MANAGEABLE') {
    throw new SubscriptionNotManageableError(
      typeof body.status === 'string' && body.status.length > 0 ? body.status : 'unknown',
    );
  }
  if (typeof body.error === 'string') throw new Error(body.error);
  if (body.error != null) throw new Error(String(body.error));
}

/**
 * supabase-js legt bei non-2xx die rohe Response unter `error.context` ab und
 * setzt `data` auf null (Muster: lib/reauth.ts). Der JSON-Body traegt die
 * Wahrheit (`code`/`error`), die `message` ist immer nur "Edge Function
 * returned a non-2xx status code".
 *
 * 409 heisst NICHT automatisch "kein Abo": die Function antwortet auch mit
 * 409 SCHEDULE_PENDING (pendender Bandwechsel blockiert die Aenderung) und
 * 409 SUBSCRIPTION_NOT_MANAGEABLE (past_due etc.). Nur der explizite Code
 * NO_ACTIVE_SUBSCRIPTION schickt den Nutzer in den Checkout-Flow. Ein 409
 * OHNE parsebaren Body oder bekannten Code bleibt ein GENERISCHER Fehler
 * (MINOR-1) — der fruehere Status-Fallback haette einen unbekannten 409
 * faelschlich in den Checkout umgeleitet.
 */
async function classifyInvokeError(error: unknown): Promise<never> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    let body: ManageErrorBody | null = null;
    try {
      // Klonen, damit ein zweiter Leser den Body noch bekommt.
      body = await (typeof ctx.clone === 'function' ? ctx.clone() : ctx).json();
    } catch {
      // Body war kein JSON oder schon konsumiert — generischer Fehler unten.
    }
    throwClassifiedBody(body);
  }
  throw error instanceof Error ? error : new Error(String(error));
}

async function invokeManageSubscription(
  body: Record<string, unknown>,
): Promise<ManageSubscriptionResult> {
  const { data, error } = await supabase.functions.invoke('manage-subscription', { body });
  if (error) await classifyInvokeError(error);
  throwClassifiedBody(data as ManageErrorBody | null);
  const status: ManageSubscriptionResult['status'] =
    data?.status === 'scheduled' ? 'scheduled'
      : data?.status === 'released' ? 'released'
      : 'updated';
  return {
    status,
    effective_at: typeof data?.effective_at === 'string' ? data.effective_at : undefined,
  };
}

/**
 * Bandwechsel eines Bestandskunden auf das Band hinter `lookupKey`. Der Wechsel
 * aufs AKTUELLE Band ist der Abbruch-Pfad: der Server released dann einen
 * pendenden Downgrade-Schedule und antwortet `{status:'released'}`.
 */
export function setBand(lookupKey: string): Promise<ManageSubscriptionResult> {
  return invokeManageSubscription({ action: 'set_band', lookupKey });
}

/** Setzt die GEBUCHTE Add-on-Menge (absolut, nicht Delta) fuer MC/Zielsprache. */
export function setAddon(
  addon: 'mc' | 'language',
  quantity: number,
): Promise<ManageSubscriptionResult> {
  return invokeManageSubscription({ action: 'set_addon', addon, quantity });
}
